import { useAuth } from "@clerk/clerk-react";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { ArrowUpDown } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import type { Id } from "#/../convex/_generated/dataModel";
import { ActivePills } from "#/components/browse/ActivePills";
import { FilterBar } from "#/components/browse/FilterBar";
import { SearchInput } from "#/components/browse/SearchInput";
import type { ListingCardData } from "#/components/listing-card";
import { ListingCard } from "#/components/listing-card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "#/components/ui/select";
import { useCityCoords } from "#/hooks/useLocationData";
import { useSignInModal } from "#/hooks/useSignInModal";
import {
	applySort,
	type FilterState,
	filterStateToQueryArgs,
	hasActiveFilters,
	toArray,
	toInt,
} from "#/lib/listingFilters";

type SortKey = "newest" | "price-asc" | "price-desc";

export const Route = createFileRoute("/listings/")({
	head: () => ({
		meta: [
			{ title: "Browse Disc Golf Listings | Re:Disc" },
			{
				name: "description",
				content:
					"Find used disc golf discs for sale. Filter by disc model, brand, condition, weight, and flight numbers. Free to contact sellers — no fees.",
			},
			{ property: "og:title", content: "Browse Disc Golf Listings | Re:Disc" },
			{
				property: "og:description",
				content:
					"Find used disc golf discs for sale. Filter by model, brand, condition, weight, and flight numbers.",
			},
		],
	}),
	validateSearch: (search: Record<string, unknown>) => ({
		sort: (["newest", "price-asc", "price-desc"].includes(search.sort as string)
			? (search.sort as SortKey)
			: "newest") as SortKey,
		q: typeof search.q === "string" ? search.q : "",
		discClass: toArray(search.discClass),
		condition: toArray(search.condition),
		color: toArray(search.color),
		shipsTo: toArray(search.shipsTo),
		country: toArray(search.country),
		plasticType: toArray(search.plasticType),
		priceMin: toInt(search.priceMin),
		priceMax: toInt(search.priceMax),
		weightMin: toInt(search.weightMin),
		weightMax: toInt(search.weightMax),
		discModelIds: toArray(search.discModelIds),
		manufacturerIds: toArray(search.manufacturerIds),
		speedMin: toInt(search.speedMin),
		speedMax: toInt(search.speedMax),
		glideMin: toInt(search.glideMin),
		glideMax: toInt(search.glideMax),
		turnMin: toInt(search.turnMin),
		turnMax: toInt(search.turnMax),
		fadeMin: toInt(search.fadeMin),
		fadeMax: toInt(search.fadeMax),
		distanceKm: toInt(search.distanceKm),
	}),
	component: ListingsPage,
});

function ListingsPage() {
	const { t } = useTranslation("listings");
	const { isSignedIn } = useAuth();
	const { open: openSignIn } = useSignInModal();
	const { data: watchlistItems = [] } = useQuery(
		convexQuery(api.watchlist.getWatchlist, {}),
	);
	const { data: userSettings } = useQuery(
		convexQuery(api.users.getMySettings, {}),
	);
	const savedIds = useMemo(
		() => new Set(watchlistItems.map((item) => item._id as string)),
		[watchlistItems],
	);
	const saveToWatchlist = useMutation(api.watchlist.saveToWatchlist);
	const removeFromWatchlist = useMutation(api.watchlist.removeFromWatchlist);

	const userCoords = useCityCoords(userSettings?.country, userSettings?.city);

	function handleSaveToggle(listingId: Id<"listings">) {
		if (!isSignedIn) {
			openSignIn();
			return;
		}
		if (savedIds.has(listingId)) {
			removeFromWatchlist({ listingId });
		} else {
			saveToWatchlist({ listingId });
		}
	}

	const {
		sort,
		q,
		discClass,
		condition,
		color,
		shipsTo,
		country,
		plasticType,
		priceMin,
		priceMax,
		weightMin,
		weightMax,
		discModelIds,
		manufacturerIds,
		speedMin,
		speedMax,
		glideMin,
		glideMax,
		turnMin,
		turnMax,
		fadeMin,
		fadeMax,
		distanceKm,
	} = Route.useSearch();
	const navigate = useNavigate({ from: "/listings/" });

	const SORT_OPTIONS: { value: SortKey; label: string }[] = [
		{ value: "newest", label: t("sort.newest") },
		{ value: "price-asc", label: t("sort.priceAsc") },
		{ value: "price-desc", label: t("sort.priceDesc") },
	];

	const filters: FilterState = {
		q,
		discClass,
		condition,
		color,
		shipsTo,
		country,
		plasticType,
		priceMin,
		priceMax,
		weightMin,
		weightMax,
		discModelIds,
		manufacturerIds,
		speedMin,
		speedMax,
		glideMin,
		glideMax,
		turnMin,
		turnMax,
		fadeMin,
		fadeMax,
		distanceKm,
	};

	const distanceArgs =
		distanceKm !== "" && userCoords
			? { latCenter: userCoords.lat, lngCenter: userCoords.lng, distanceKm }
			: {};

	const { q: _q, ...listQueryArgs } = filterStateToQueryArgs(filters);
	const { data: browseListings = [], isPending: browsePending } = useQuery({
		...convexQuery(api.listings.list, { ...listQueryArgs, ...distanceArgs }),
		enabled: !q,
	});
	const { data: searchListings = [], isPending: searchPending } = useQuery({
		...convexQuery(api.listings.searchListings, {
			...listQueryArgs,
			...distanceArgs,
			q: q ?? "",
		}),
		enabled: !!q,
	});
	const serverListings = q ? searchListings : browseListings;
	const isPending = q ? searchPending : browsePending;

	const availableCountries = useMemo(() => {
		const countries = new Set(
			(serverListings as ListingCardData[])
				.map((l) => l.country)
				.filter(Boolean),
		);
		return Array.from(countries).sort();
	}, [serverListings]);

	const availablePlasticNames = useMemo((): string[] | null => {
		if (manufacturerIds.length === 0) return null;
		const names = new Set(
			(serverListings as ListingCardData[])
				.filter(
					(l) => l.manufacturerId && manufacturerIds.includes(l.manufacturerId),
				)
				.map((l) => l.plasticType),
		);
		return Array.from(names).sort();
	}, [serverListings, manufacturerIds]);

	const listings = useMemo(
		() => applySort(serverListings as ListingCardData[], sort),
		[serverListings, sort],
	);

	function handleSortChange(value: string) {
		navigate({
			search: (prev) => ({ ...prev, sort: value as SortKey }),
			replace: true,
			viewTransition: false,
		});
	}

	function handleFilterChange(next: Partial<FilterState>) {
		navigate({
			search: (prev) => ({ ...prev, ...next }),
			replace: true,
			viewTransition: false,
		});
	}

	const hasFilters = hasActiveFilters(filters);

	return (
		<div className="min-h-screen">
			{/* Page header */}
			<div className="border-b border-border">
				<div className="page-wrap py-10">
					<p className="sec-tag mb-2">{t("header.tag")}</p>
					<div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
						<h1 className="display-title text-[clamp(36px,4vw,52px)] leading-none text-foreground">
							{t("header.title")}
						</h1>
						<div className="flex w-full items-center gap-3 pb-0.5 sm:w-auto">
							<div className="flex-1 sm:flex-none">
								<SearchInput value={q} />
							</div>
							<div className="flex shrink-0 items-center gap-2">
								<ArrowUpDown className="size-3.5 text-muted-foreground" />
								<Select value={sort} onValueChange={handleSortChange}>
									<SelectTrigger
										size="sm"
										className="w-[160px]"
										aria-label={t("sort.label")}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent align="end">
										{SORT_OPTIONS.map((opt) => (
											<SelectItem key={opt.value} value={opt.value}>
												{opt.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>
					<p
						className={`mt-3 text-sm text-muted-foreground transition-opacity ${isPending ? "invisible" : ""}`}
					>
						{listings.length === 0
							? t("resultCount_zero")
							: t("resultCount", { count: listings.length })}
					</p>
				</div>
			</div>

			{/* Filter bar */}
			<div className="border-b border-border bg-muted/30">
				<div className="page-wrap py-3">
					<FilterBar
						filters={filters}
						availableCountries={availableCountries}
						availablePlasticNames={availablePlasticNames}
						onChange={handleFilterChange}
						userCity={userSettings?.city ?? null}
						userHasCoords={userCoords !== null}
					/>
					{hasFilters && (
						<div className="mt-2.5">
							<ActivePills filters={filters} onChange={handleFilterChange} />
						</div>
					)}
				</div>
			</div>

			{/* Listing grid */}
			<div className="page-wrap py-10 min-h-[600px]">
				{isPending ? (
					<SkeletonGrid />
				) : listings.length === 0 ? (
					hasFilters ? (
						<NoResultsState
							onClear={() =>
								handleFilterChange({
									q: "",
									discClass: [],
									condition: [],
									color: [],
									shipsTo: [],
									country: [],
									plasticType: [],
									priceMin: "",
									priceMax: "",
									weightMin: "",
									weightMax: "",
									discModelIds: [],
									manufacturerIds: [],
									speedMin: "",
									speedMax: "",
									glideMin: "",
									glideMax: "",
									turnMin: "",
									turnMax: "",
									fadeMin: "",
									fadeMax: "",
									distanceKm: "",
								})
							}
						/>
					) : (
						<EmptyState />
					)
				) : (
					<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{listings.map((listing, i) => (
							<div
								key={listing._id}
								className="rise-in"
								style={{ animationDelay: `${Math.min(i * 25, 250)}ms` }}
							>
								<ListingCard
									listing={listing}
									isSaved={savedIds.has(listing._id)}
									onSaveToggle={() => handleSaveToggle(listing._id)}
									eager={i < 4}
									priority={i === 0}
								/>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

function SkeletonGrid() {
	return (
		<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{Array.from({ length: 8 }, (_, i) => `skeleton-${i}`).map((id) => (
				<div
					key={id}
					className="overflow-hidden rounded-[var(--radius)] border border-border bg-card"
				>
					<div className="h-[230px] animate-pulse bg-muted" />
					<div className="flex flex-col gap-3 px-4 pb-4 pt-3">
						<div className="flex items-end justify-between gap-2">
							<div className="flex flex-col gap-1.5">
								<div className="h-2 w-16 animate-pulse rounded-full bg-muted" />
								<div className="h-4 w-32 animate-pulse rounded-full bg-muted" />
							</div>
							<div className="h-4 w-10 animate-pulse rounded-full bg-muted" />
						</div>
						<div className="h-10 animate-pulse rounded-lg bg-muted" />
						<div className="h-3 w-24 animate-pulse rounded-full bg-muted" />
					</div>
				</div>
			))}
		</div>
	);
}

function EmptyState() {
	const { t } = useTranslation("listings");
	return (
		<div className="flex flex-col items-center py-28 text-center">
			<div className="relative mb-8 h-24 w-24 opacity-15">
				<div className="h-full w-full rounded-full border-[12px] border-foreground" />
				<div className="absolute inset-[16px] rounded-full border-[4px] border-foreground" />
				<div className="absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground" />
			</div>
			<p className="text-xl font-bold text-foreground">{t("empty.title")}</p>
			<p className="mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">
				{t("empty.description")}
			</p>
			<Link
				to="/sell"
				className="mt-6 inline-flex items-center rounded-[var(--radius)] bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
			>
				{t("empty.cta")}
			</Link>
		</div>
	);
}

function NoResultsState({ onClear }: { onClear: () => void }) {
	const { t } = useTranslation("listings");
	return (
		<div className="flex flex-col items-center py-24 text-center">
			<div className="relative mb-6 h-16 w-16 opacity-20">
				<div className="h-full w-full rounded-full border-[8px] border-foreground" />
				<div className="absolute inset-[11px] rounded-full border-[3px] border-foreground" />
				<div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground" />
			</div>
			<p className="text-lg font-bold text-foreground">
				{t("noResults.title")}
			</p>
			<p className="mt-1.5 max-w-xs text-sm text-muted-foreground">
				{t("noResults.description")}
			</p>
			<button
				type="button"
				onClick={onClear}
				className="mt-5 text-sm font-semibold text-primary-accessible hover:underline"
			>
				{t("noResults.clear")}
			</button>
		</div>
	);
}
